import { Icon, InputGroup, Pill, Text } from "@smartsuite/react-ui/lib";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as yup from "yup";

import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormContext } from "../../../../hooks/useFormContext";
import { useFormField } from "../../../../hooks/useFormField";
import type { LinkedRecordObject } from "../../../../types/form";
import { $t } from "../../../../utils/intl";
import type { FormFieldProps } from "../../../types";
import { validateWithYup } from "../../../validator";
import { validatorText } from "../../../validator.text";
import { linkedRecordPillStyles } from "../linked-record.constants";
import "./LinkedRecordFieldControl.sass";
import { GrayColors } from "@smartsuite/types";
import { SolutionColorVariables } from "@smartsuite/types/lib/constants/color";
import { usePrefillContext } from "../../../../hooks/usePrefillContext";
import { IFRAME_MESSAGES, postMessageToParent } from "../../../../utils/iframeMessages";
import { fieldsText } from "../../fields.text";
import { prefillLinkedRecord } from "../linked-record.prefill";
import { LinkedRecordTable } from "./components/LinkedRecordTable/LinkedRecordTable";
import { SelectLinkedRecordDropdown } from "./components/SelectLinkedRecordDropdown/SelectLinkedRecordDropdown";
import { SelectLinkedRecordModal } from "./components/SelectLinkedRecordModal/SelectLinkedRecordModal";
import { useLinkedRecords } from "./hooks/useLinkedRecords";

export type LinkedRecordFieldControlProps = FormFieldProps;

export const LinkedRecordFieldControl: React.FunctionComponent<LinkedRecordFieldControlProps> = ({
  caption,
  field,
  formItem,
  label,
  name,
  required,
  readOnly,
  isItemConditionSatisfied,
}) => {
  const [hovered, setHovered] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isMultiple = field.params?.entries_allowed === "multiple";
  const recordSelectType: "modal" | "dropdown" = formItem.params.record_select_type || "modal";

  const requiredText = $t(validatorText.validationRequired);
  const formField = useFormField({
    name,
    validate: validateWithYup(
      yup
        .array()
        .of(yup.object())
        .when({
          is: () => formItem.required && !formItem.params.read_only && isItemConditionSatisfied,
          then: (lr) => lr.required(requiredText).min(1, requiredText),
        }),
    ),
  });

  const { config } = useFormContext();

  const { prefillValues } = usePrefillContext();

  const prefillValue = prefillValues.dynamicFields[name];

  const isTableDisplay =
    config.feature_flags?.form_linked_record_table_display === true &&
    formItem.params.display_type === "table";

  const initialValueIds = useMemo<string[]>(() => {
    if (!Array.isArray(formField.value)) return [];
    return formField.value.filter(
      (item): item is string => typeof item === "string" && item.length > 0,
    );
  }, [formField.value]);

  const hasUnhydratedStringIds =
    Array.isArray(formField.value) &&
    formField.value.length > 0 &&
    formField.value.length === initialValueIds.length;

  const hasFormSchemaDefault =
    config.feature_flags?.lr_default_value === true && hasUnhydratedStringIds;

  const linkedRecords = useLinkedRecords({
    field,
    formItem,
    formField,
    enabled:
      !!prefillValue || formField.state === "active" || hasFormSchemaDefault || isTableDisplay,
  });

  const handleSelectChange = (changeValue: LinkedRecordObject | LinkedRecordObject[]): void => {
    if (changeValue) {
      if (Array.isArray(changeValue)) {
        formField.onChange(changeValue);
      } else {
        formField.onChange([changeValue]);
      }
    } else {
      formField.onChange([]);
    }
  };

  const handleInputClick = (): void => {
    setIsModalOpen(true);
    postMessageToParent(IFRAME_MESSAGES.MODAL_OPENED);
  };

  const handleRowClick = useCallback(() => {
    setIsModalOpen(true);
    postMessageToParent(IFRAME_MESSAGES.MODAL_OPENED);
  }, []);

  const handleRemove = (id: string): void => {
    formField.onChange(
      formField.value.filter((linkedRecord: LinkedRecordObject) => linkedRecord.id !== id),
    );
  };

  useEffect(() => {
    const canApplyPrefill = formField.value === undefined || hasUnhydratedStringIds;

    if (prefillValue && !linkedRecords.isRecordsLoading && canApplyPrefill) {
      const parsedPrefillValue = prefillLinkedRecord({
        value: prefillValue,
        field,
        linkedRecords: linkedRecords.records,
      });

      handleSelectChange(parsedPrefillValue as LinkedRecordObject[]);
    }
  }, [prefillValue, linkedRecords.isRecordsLoading, hasUnhydratedStringIds]);

  useEffect(() => {
    if (
      !hasFormSchemaDefault ||
      prefillValue ||
      linkedRecords.isRecordsLoading ||
      !Array.isArray(formField.value) ||
      formField.value.length === 0
    ) {
      return;
    }

    const idsAsString = (formField.value as string[]).join(",");
    const hydrated = prefillLinkedRecord({
      value: idsAsString,
      field,
      linkedRecords: linkedRecords.records,
    });
    handleSelectChange(hydrated as LinkedRecordObject[]);
  }, [hasFormSchemaDefault, prefillValue, linkedRecords.isRecordsLoading]);

  if (!isItemConditionSatisfied) return null;

  return (
    <>
      <FormFieldControl
        captionAsTooltip={formItem.params.help_text_as_tooltip}
        caption={caption}
        errorMessage={formField.errorMessage}
        label={label}
        required={required}
        helpText={formItem.params.helpText}
        state={formField.state}
        readOnly={readOnly}
      >
        {isTableDisplay ? (
          <LinkedRecordTable
            field={field}
            formItem={formItem}
            formField={formField}
            linkedRecords={linkedRecords}
            readOnly={readOnly}
            isMultiple={isMultiple}
            onLinkRecordClick={handleInputClick}
            onChangeFocused={formField.onChangeFocused}
            onRowClick={handleRowClick}
          />
        ) : (
          <>
            {recordSelectType === "modal" && (
              <InputGroup
                className="linked-record-control__input"
                inputBackground="white-new"
                state={formField.state}
                onClick={handleInputClick}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
              >
                {!formField.value?.length && (
                  <Text className="placeholder" color="secondary">
                    {isMultiple
                      ? $t(fieldsText.multipleSelectPlaceholder)
                      : $t(fieldsText.singleSelectPlaceholder)}
                  </Text>
                )}
                {formField.value?.map((linkedRecord: LinkedRecordObject) => (
                  <Pill
                    key={linkedRecord.id}
                    className={"linked-record-standard-select__item"}
                    text={linkedRecord.title}
                    styles={linkedRecordPillStyles}
                    animatedRemove={true}
                    onClick={handleInputClick}
                    removable
                    onRemove={() => handleRemove(linkedRecord.id)}
                  />
                ))}
                {!readOnly && (
                  <Icon
                    className="linked-record-control__icon"
                    name="arrow-down-smaller"
                    size="S"
                    color={hovered ? SolutionColorVariables.sc1 : GrayColors["G-7"]}
                  />
                )}
              </InputGroup>
            )}

            {recordSelectType === "dropdown" && (
              <SelectLinkedRecordDropdown
                readOnly={readOnly}
                linkedRecords={linkedRecords.selectableRecords}
                field={field}
                formField={formField}
                formItem={formItem}
                handleSelectChange={handleSelectChange}
              />
            )}
          </>
        )}
      </FormFieldControl>
      {isModalOpen && (
        <SelectLinkedRecordModal
          formField={formField}
          formItem={formItem}
          field={field}
          alreadySelectedRecords={formField.value}
          onSave={handleSelectChange}
          onClose={() => {
            setIsModalOpen(false);
            postMessageToParent(IFRAME_MESSAGES.MODAL_CLOSED);
          }}
        />
      )}
    </>
  );
};
